import { readScopedString, writeScopedString } from '../utils/scopedStorage'

export const DEFAULT_PRODUCTION_SPECIES = 'trout'

export type ProductionSpeciesKey = 'trout' | 'tilapia' | 'salmon' | 'shrimp'

export type ProductionSpeciesPreset = {
  key: ProductionSpeciesKey
  label: string
  description: string
  columns: string[]
  importAliases: Record<string, string>
  expectedFcrRange: {
    min: number
    max: number
  }
}

const normalize = (value: string): string =>
  value
    .toString()
    .trim()
    // #46: strip accent marks BEFORE lowercasing so Spanish-locale headers like
    // "Población", "Oxígeno", "Tratamientos" normalize to their accent-less
    // forms and can match aliases without listing every diacritic variant.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

const GLOBAL_IMPORT_ALIASES_RAW: Record<string, string> = {
  treatment: 'mort',
  treatments: 'mort',
  mortality: 'dailyMortality',
  dailymortality: 'dailyMortality',
  losses: 'dailyMortality',
  fishstock: 'fishStock',
  fishstocks: 'fishStock',
  fishcount: 'fishStock',
  fishcounttotal: 'fishStock',
  fish_count: 'fishStock',
  stockcount: 'fishStock',
  avgweight: 'weight',
  averageweight: 'weight',
  averageweightg: 'weight',
  avgweightg: 'weight',
  avgweightgrams: 'weight',
  biomass: 'weight',
  biomasskg: 'weight',
  weightkg: 'weight',
  avglen: 'avgLengthMm',
  avglength: 'avgLengthMm',
  averagelength: 'avgLengthMm',
  averagelengthmm: 'avgLengthMm',
  lengthmm: 'avgLengthMm',
  length: 'avgLengthMm',
  comments: 'upVol',
  comment: 'upVol',
  note: 'upVol',
  notes: 'upVol',
  observation: 'upVol',
  observations: 'upVol',
  // allow CSV fields to target canonical casing
  do: 'do',
  // #46: Spanish-locale aliases. Operators in Latin America commonly export
  // CSVs whose headers are in Spanish ("Lote", "Fecha", "Peso", "Mortalidad",
  // …). Surfaced by the user's `test 2 database 102.csv`. Diacritics are
  // stripped during normalize(), so accented variants (e.g. "Población",
  // "Cosecha Kg") match the unaccented form here.
  fecha: 'date',
  lote: 'lot',
  peso: 'weight',
  pesopromedio: 'weight',
  pesomedio: 'weight',
  pesog: 'weight',
  pesogramos: 'weight',
  biomasa: 'weight',
  biomasakg: 'weight',
  mortalidad: 'dailyMortality',
  mortalidaddiaria: 'dailyMortality',
  bajas: 'dailyMortality',
  muertes: 'dailyMortality',
  poblacion: 'fishStock',
  numeropeces: 'fishStock',
  numerodepeces: 'fishStock',
  cantidadpeces: 'fishStock',
  longitud: 'avgLengthMm',
  longitudpromedio: 'avgLengthMm',
  largomm: 'avgLengthMm',
  salud: 'health',
  estado: 'health',
  etapa: 'stage',
  fase: 'stage',
  observaciones: 'upVol',
  observacion: 'upVol',
  notas: 'upVol',
  nota: 'upVol',
  comentarios: 'upVol',
  comentario: 'upVol',
  cosecha: 'harvestKg',
  cosechakg: 'harvestKg',
  tratamiento: 'mort',
  tratamientos: 'mort'
}

const GLOBAL_IMPORT_ALIASES: Record<string, string> = Object.entries(GLOBAL_IMPORT_ALIASES_RAW).reduce(
  (acc, [alias, column]) => {
    acc[normalize(alias)] = column
    return acc
  },
  {} as Record<string, string>
)

export const PRODUCTION_SPECIES_PRESETS: Record<ProductionSpeciesKey, ProductionSpeciesPreset> = {
  trout: {
    key: 'trout',
    label: 'Rainbow Trout',
    description: 'Cold-water preset for salmonids; includes FCR and length tracking.',
    columns: [
      'lot',
      'date',
      'weight',
      'dailyMortality',
      'fishStock',
      'harvestKg',
      'harvestCount',
      'avgLengthMm',
      'feedKg',
      'fcr',
      'health',
      'mort',
      'upVol'
    ],
    importAliases: {
      feed: 'feedKg',
      feedkg: 'feedKg',
      feedration: 'feedKg',
      rationkg: 'feedKg',
      feedamount: 'feedKg',
      feedamountkg: 'feedKg',
      feedusedkg: 'feedKg',
      feedused: 'feedKg',
      fcratio: 'fcr',
      feedconversionratio: 'fcr',
      fcrcalc: 'fcr'
    },
    expectedFcrRange: { min: 0.9, max: 1.4 }
  },
  tilapia: {
    key: 'tilapia',
    label: 'Nile Tilapia',
    description: 'Warm-water preset with feed protein and temperature checkpoints.',
    columns: [
      'lot',
      'date',
      'weight',
      'dailyMortality',
      'fishStock',
      'harvestKg',
      'harvestCount',
      'avgLengthMm',
      'feedKg',
      'feedProteinPct',
      'temperatureC',
      'health',
      'mort',
      'upVol'
    ],
    importAliases: {
      feed: 'feedKg',
      feedkg: 'feedKg',
      feedamount: 'feedKg',
      feedamountkg: 'feedKg',
      rationkg: 'feedKg',
      protein: 'feedProteinPct',
      feedprotein: 'feedProteinPct',
      feedproteinpct: 'feedProteinPct',
      proteinpct: 'feedProteinPct',
      proteinpercent: 'feedProteinPct',
      tempc: 'temperatureC',
      temperature: 'temperatureC',
      watertemp: 'temperatureC',
      watertemperature: 'temperatureC'
    },
    expectedFcrRange: { min: 1.2, max: 1.8 }
  },
  salmon: {
    key: 'salmon',
    label: 'Atlantic Salmon',
    description: 'Marine net-pen preset with salinity capture.',
    columns: [
      'lot',
      'date',
      'weight',
      'dailyMortality',
      'fishStock',
      'harvestKg',
      'harvestCount',
      'avgLengthMm',
      'feedKg',
      'temperatureC',
      'salinityPpt',
      'health',
      'mort',
      'upVol'
    ],
    importAliases: {
      feed: 'feedKg',
      feedkg: 'feedKg',
      feedamount: 'feedKg',
      feedamountkg: 'feedKg',
      watertemp: 'temperatureC',
      watertemperature: 'temperatureC',
      temperature: 'temperatureC',
      tempc: 'temperatureC',
      salinity: 'salinityPpt',
      salinityppt: 'salinityPpt',
      salinitypsu: 'salinityPpt'
    },
    expectedFcrRange: { min: 1.0, max: 1.5 }
  }
  ,
  shrimp: {
    key: 'shrimp',
    label: 'Pacific White Shrimp',
    description: 'Crustacean preset (PL terminology) with salinity + temperature emphasis.',
    columns: [
      'lot',
      'date',
      'weight',
      'dailyMortality',
      'fishStock',
      'harvestKg',
      'harvestCount',
      'temperatureC',
      'salinityPpt',
      'feedKg',
      'fcr',
      'health',
      'mort',
      'upVol'
    ],
    importAliases: {
      // PL / animal count terminology (maps into canonical fishStock)
      pl: 'fishStock',
      plcount: 'fishStock',
      postlarvae: 'fishStock',
      postlarvaecount: 'fishStock',
      shrimpcount: 'fishStock',
      animals: 'fishStock',
      animalcount: 'fishStock',
      count: 'fishStock',
      // environment
      tempc: 'temperatureC',
      temperature: 'temperatureC',
      watertemp: 'temperatureC',
      watertemperature: 'temperatureC',
      salinity: 'salinityPpt',
      salinityppt: 'salinityPpt',
      salinitypsu: 'salinityPpt',
      // feed
      feed: 'feedKg',
      feedkg: 'feedKg',
      feedamount: 'feedKg',
      feedamountkg: 'feedKg',
      fcratio: 'fcr',
      feedconversionratio: 'fcr'
    },
    expectedFcrRange: { min: 1.0, max: 1.6 }
  }
}

export const PRODUCTION_SPECIES_OPTIONS = Object.values(PRODUCTION_SPECIES_PRESETS)
  .map(({ key, label, description }) => ({ key, label, description }))

export const normalizeSpeciesKey = (value: string | undefined | null): ProductionSpeciesKey => {
  if (!value) return DEFAULT_PRODUCTION_SPECIES
  const normalized = value.toLowerCase() as ProductionSpeciesKey
  return normalized in PRODUCTION_SPECIES_PRESETS ? normalized : DEFAULT_PRODUCTION_SPECIES
}

export const getPresetColumns = (species: string): string[] => {
  const key = normalizeSpeciesKey(species)
  return PRODUCTION_SPECIES_PRESETS[key].columns.slice()
}

export const buildImportAliasMap = (species: string): Record<string, string> => {
  const key = normalizeSpeciesKey(species)
  const preset = PRODUCTION_SPECIES_PRESETS[key]
  const merged: Record<string, string> = { ...GLOBAL_IMPORT_ALIASES }
  Object.entries(preset.importAliases).forEach(([alias, column]) => {
    merged[normalize(alias)] = column
  })
  return merged
}

export const normalizeHeader = (value: string): string => normalize(value)

export const resolveExpectedFcrRange = (species: string | undefined | null): { min: number; max: number } | null => {
  const key = normalizeSpeciesKey(species)
  return PRODUCTION_SPECIES_PRESETS[key]?.expectedFcrRange ?? null
}

const SPECIES_STORAGE_KEY = 'productionDatabase:selectedSpecies'

export const loadStoredSpecies = (): ProductionSpeciesKey => {
  const stored = readScopedString(SPECIES_STORAGE_KEY)
  if (!stored) return DEFAULT_PRODUCTION_SPECIES
  return normalizeSpeciesKey(stored)
}

export const persistSpeciesSelection = (species: string): void => {
  const key = normalizeSpeciesKey(species)
  writeScopedString(SPECIES_STORAGE_KEY, key)
}
